/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraController,
  GamoraControllerStateContext,
  GamoraControllerStateMap,
  GamoraControllerTransitionContextInput,
} from '../controller.js';
import { assert } from '../../utils/assert.js';
import { GamoraConstructorType } from '../../foundation/generic.js';
import { GamoraErrorNotImplemented } from '../../errors/not-implemented.error.js';
import { GamoraFoundationGlue } from '../../foundation/glue/glue.js';
import { GamoraFoundationNodeRoot } from '../../foundation/node/node.js';
import { GamoraUtilsStrandPromise } from '../../utils/strand.js';

/**
 * GamoraControllerStateConstructor
 *
 * A constructor for a base type
 * @internal
 */
export type GamoraControllerStateConstructor = GamoraConstructorType<
  GamoraControllerState<any, any, any>
>;

/**
 * GamoraControllerStateArgs
 *
 * The controller state arguments
 * @public
 */
export interface GamoraControllerStateArgs {
  root?: GamoraFoundationNodeRoot;
}

/**
 * GamoraControllerState
 *
 * Abstract class that provides a state
 * @public
 */
export abstract class GamoraControllerState<
  TStateMap extends GamoraControllerStateMap,
  TStateContext extends GamoraControllerStateContext,
  TRoot extends GamoraFoundationNodeRoot = any,
> extends GamoraFoundationGlue<TStateMap, TRoot> {
  private _$controller:
    | GamoraController<TStateMap, TStateContext, TRoot>
    | undefined;

  constructor(args: GamoraControllerStateArgs) {
    super({ root: args.root });
  }

  /**
   * Gets the controller
   */
  get $controller() {
    assert(
      this._$controller !== undefined,
      'Invalid $controller: not defined.',
    );
    return this._$controller;
  }

  /**
   * Set the controller
   */
  set $controller(
    controller: GamoraController<TStateMap, TStateContext, TRoot>,
  ) {
    assert(
      controller instanceof GamoraController,
      'Invalid controller argument: not a GamoraController.',
    );
    this._$controller = controller;
  }

  /**
   * Disable getChild for state nodes
   */
  override $getChild<K extends keyof TStateMap>(): InstanceType<TStateMap[K]> {
    throw new GamoraErrorNotImplemented();
  }

  /**
   * Enter state
   * @param stateContext - The state context
   */
  async $enter(stateContext: TStateContext): Promise<void> {
    assert(
      typeof stateContext === 'object' && stateContext !== null,
      'Invalid stateContext argument: not an object.',
    );
    throw new GamoraErrorNotImplemented(
      `${this.constructor.name} has no $enter() implementation.`,
    );
  }

  /**
   * Exit state
   * @param stateContext - The state context
   */
  async $exit(stateContext: TStateContext): Promise<void> {
    assert(
      typeof stateContext === 'object' && stateContext !== null,
      'Invalid stateContext argument: not an object.',
    );
    throw new GamoraErrorNotImplemented(
      `${this.constructor.name} has no $exit() implementation.`,
    );
  }

  /**
   * Checks if the calling state is the current state
   * @param stateContext - The state context
   */
  $isInCurrentState(stateContext: TStateContext): boolean {
    assert(
      typeof stateContext === 'object' && stateContext !== null,
      'Invalid stateContext argument: not an object.',
    );

    // Ensure state context exists
    if (!this.$controller.$hasContext(stateContext.contextIdentifier)) {
      return false;
    }

    // Checks if state is active
    const controllerContext = this.$controller.$getContext(
      stateContext.contextIdentifier,
    );
    assert(
      controllerContext.stateCurrent,
      'Invalid controllerContext: stateCurrent is not set.',
    );
    const activeState = this.$controller.$(controllerContext.stateCurrent);
    return activeState === this;
  }

  /**
   * Checks if the calling state is the current state
   * @param stateContext - The state context
   */
  $hasPostedTransition(stateContext: TStateContext): boolean {
    assert(
      typeof stateContext === 'object' && stateContext !== null,
      'Invalid stateContext argument: not an object.',
    );

    // Ensure state context exists
    if (!this.$controller.$hasContext(stateContext.contextIdentifier)) {
      return false;
    }

    // Checks if state is active
    const controllerContext = this.$controller.$getContext(
      stateContext.contextIdentifier,
    );
    return controllerContext.transitionPosted;
  }

  /**
   * Tries posting task to context strand, if it still exists. It is only executed
   * when in current state and if the context still exists.
   * @param stateContext
   * @returns
   */
  $tryPostTask<TTaskReturn>(
    stateContext: TStateContext,
    func: (stateContext: TStateContext) => Promise<TTaskReturn>,
  ): GamoraUtilsStrandPromise<TTaskReturn | undefined> | undefined {
    // Exit early if context no longer exists
    if (!this.$controller.$hasContext(stateContext.contextIdentifier)) {
      return;
    }

    const context = this.$controller.$getContext(
      stateContext.contextIdentifier,
    );

    // Execute via context strand
    return context.contextStrand.post(async () => {
      // Only continue when current state is current
      if (!this.$isInCurrentState(context.stateContext)) {
        return;
      }

      // Execute task with args
      return await func(context.stateContext);
    });
  }

  /**
   * Post task to context strand and execute
   * @param stateContext
   * @returns
   */
  $postTask<TTaskReturn>(
    stateContext: TStateContext,
    func: (stateContext: TStateContext) => Promise<TTaskReturn>,
  ): GamoraUtilsStrandPromise<TTaskReturn> {
    assert(
      this.$controller.$hasContext(stateContext.contextIdentifier),
      'Invalid stateContext: context does not exist.',
    );
    const context = this.$controller.$getContext(
      stateContext.contextIdentifier,
    );

    // Execute via context strand
    return context.contextStrand.post(async () => {
      assert(
        this.$controller.$hasContext(stateContext.contextIdentifier),
        'Invalid stateContext: context does not exist.',
      );
      assert(
        this.$isInCurrentState(context.stateContext),
        'Invalid currentState: context is not in original posted state.',
      );

      // Execute task with args
      return await func(stateContext);
    });
  }

  /**
   * Transition state
   * @note Wait on this promise with care to avoid strand locking issues
   * @param stateContext - The state context
   * @param to - The next state
   */
  $postTransition<
    TInput extends GamoraControllerTransitionContextInput<TStateMap>,
  >(
    stateContext: TStateContext,
    input: TInput,
  ): GamoraUtilsStrandPromise<void> {
    assert(
      typeof stateContext === 'object' && stateContext !== null,
      'Invalid stateContext argument: not an object.',
    );
    assert(
      typeof input === 'object' && stateContext !== null,
      'Invalid input argument: not an object.',
    );

    // Check if context is in current state
    assert(
      this.$isInCurrentState(stateContext),
      'Invalid stateContext: context is not in current state.',
    );

    // Check if context does not already have transition added to strand
    assert(
      !this.$hasPostedTransition(stateContext),
      'Invalid stateContext: context has already transition posted.',
    );

    // Posted transition
    const controllerContext = this.$controller.$getContext(
      stateContext.contextIdentifier,
    );
    controllerContext.transitionPosted = true;

    // Transition state
    return this.$controller.$transitionContext(
      stateContext.contextIdentifier,
      input,
    );
  }

  /**
   * Try to post transition and do nothing when not in current state or an transition is already posted
   * @note Wait on this promise with care to avoid strand locking issues
   * @param stateContext - The state context
   * @param to - The next state
   */
  $tryPostTransition<
    TInput extends GamoraControllerTransitionContextInput<TStateMap>,
  >(
    stateContext: TStateContext,
    input: TInput,
  ): GamoraUtilsStrandPromise<void> | void {
    assert(
      typeof stateContext === 'object' && stateContext !== null,
      'Invalid stateContext argument: not an object.',
    );
    assert(
      typeof input === 'object' && stateContext !== null,
      'Invalid input argument: not an object.',
    );

    if (
      this.$isInCurrentState(stateContext) &&
      !this.$hasPostedTransition(stateContext)
    ) {
      // Posted transition
      const controllerContext = this.$controller.$getContext(
        stateContext.contextIdentifier,
      );
      controllerContext.transitionPosted = true;

      // Transition state
      return this.$controller.$transitionContext(
        stateContext.contextIdentifier,
        input,
      );
    }
  }

  /**
   * Clean up
   * @param stateContext - The state context
   */
  $cleanup(stateContext: TStateContext): void {
    assert(
      typeof stateContext === 'object' && stateContext !== null,
      'Invalid stateContext argument: not an object.',
    );
    // Clean up listeners
    this.$removeInvokerListenersByNamespace(stateContext.contextIdentifier);
    this.$removeYielderListenersByNamespace(stateContext.contextIdentifier);
    this.$removeEventListenersByNamespace(stateContext.contextIdentifier);
  }
}
